import { createElement, type ReactNode } from 'react';
import { type Id, type ToastOptions, toast } from 'react-toastify';

type OpenNotificationParams = {
  cancelMutation?: () => void;
  description?: string;
  key?: string;
  message: string;
  type: 'success' | 'error' | 'progress';
  undoableTimeout?: number;
};

type NotificationProvider = {
  close: (key?: string) => void;
  open: (params: OpenNotificationParams) => void;
};

type ToastFactory = (content: ReactNode, options?: ToastOptions) => Id;

const getToastFactory = (type: OpenNotificationParams['type']): ToastFactory => {
  if (type === 'success') {
    return (content, options) => toast.success(content, options);
  }
  if (type === 'error') {
    return (content, options) => toast.error(content, options);
  }
  return (content, options) => toast.loading(content, options);
};

const createToastContent = ({
  message,
  description,
  onCancel,
}: {
  message: string;
  description?: string;
  onCancel?: () => void;
}) =>
  createElement(
    'div',
    null,
    createElement('div', null, message),
    description ? createElement('div', null, description) : null,
    onCancel
      ? createElement(
          'button',
          {
            onClick: onCancel,
            type: 'button',
          },
          'Cancel'
        )
      : null
  );

const openNotification: NotificationProvider['open'] = ({
  cancelMutation,
  description,
  key,
  message,
  type,
  undoableTimeout,
}) => {
  const toastFactory = getToastFactory(type);

  // eslint-disable-next-line prefer-const
  let toastId: Id | undefined;

  const handleCancel = () => {
    cancelMutation?.();
    const idToDismiss = key ?? toastId;
    if (idToDismiss !== undefined) {
      toast.dismiss(idToDismiss);
    }
  };

  const content = createToastContent({
    description,
    message,
    onCancel: cancelMutation ? handleCancel : undefined,
  });

  const options: ToastOptions = {
    autoClose: type === 'progress' ? (undoableTimeout === undefined ? false : undoableTimeout) : undefined,
    closeButton: type !== 'progress',
    closeOnClick: type !== 'progress',
    draggable: type !== 'progress',
    hideProgressBar: true,
    toastId: key,
  };

  toastId = toastFactory(content, options);
};

const closeNotification: NotificationProvider['close'] = (key) => {
  if (key) {
    toast.dismiss(key);
    return;
  }

  toast.dismiss();
};

export const notificationProvider: NotificationProvider = {
  close: closeNotification,
  open: openNotification,
};

export const useNotification = () => notificationProvider;
